import { Button } from '@/components/ui/button';
import { differenceInDays } from 'date-fns';
import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { ChargeSummary } from 'src/components/ChargeSummary';
import { MINIMUM_WEIGHT_IN_TONS } from 'src/constants';
import { Order, Cart, trpc, OrderContainer } from 'src/trpc';
import { DiscountDialog } from './DiscountDialog';
import {
  Discount,
  getPricingAwareOrderAdjustments,
  PricingContext,
  MoneyFromCents,
} from '@libs/services/money';
import { OrderAction, OrderStatusEnum } from '@libs/schemas/src/order';
import { PricingTypeEnum } from '@libs/schemas';
import { BundlePricingForm } from './BundlePricingForm';
import { FixedPricingForm } from './FixedPricingForm';
import { PayAsYouGoPricingForm } from './PayAsYouGoPricingForm';
import { Loader } from 'components/Loader';
import { createChargeSummaryPropsFromWasteProcessing, toValidDate } from '@libs/frontend-services';

interface Props {
  order: Order;
  containers: OrderContainer[];
  cart: Cart;
}

export const WasteProcessingForm: FC<Props> = ({ order, containers, cart }) => {
  // Get organization-specific minimum weight threshold
  const { data: organization } = trpc.admin.organization.getOrganization.useQuery(
    order.organizationId || '',
    {
      enabled: !!order.organizationId,
      staleTime: Infinity,
      gcTime: Infinity,
    },
  );

  // Use organization-specific minimum weight or fallback to system default
  const minWeightInTons =
    order.minimumWeightInTons || organization?.minimumOrderWeightInTons || MINIMUM_WEIGHT_IN_TONS;

  // Determine pricing model from quotedPricingDetails
  const pricingModel = order.quotedPricingDetails?.pricingModel || PricingTypeEnum.PAY_AS_YOU_GO;

  // Memoize the initial values
  const initialWeight = useMemo(() => order.initialWeightInTons || 0, [order.initialWeightInTons]);
  const initialUsageByContainer = useMemo(() => {
    return containers.reduce(
      (acc, c) => {
        const pickup = order.containers.find(
          (co) => co.containerIds.includes(c._id) && co.action === OrderAction.Pickup,
        );
        const dropOff = order.containers.find(
          (co) => co.containerIds.includes(c._id) && co.action === OrderAction.DropOff,
        );

        const pickupDate = toValidDate(pickup?.window?.day);
        const dropOffDate = toValidDate(dropOff?.window?.day);
        let days: number;
        // Status corrections can expose assignments without complete schedules. Reuse
        // the existing unscheduled fallback instead of dereferencing a missing window
        // or feeding NaN days into the money calculations.
        if (pickupDate && dropOffDate) {
          days = Math.max(differenceInDays(pickupDate, dropOffDate), 1);
        } else {
          // Fallback: use finalizedLineItems data from a previous cycle, or default to 0
          const finalizedContainer = order.finalizedLineItems?.containers?.find(
            (fc) => fc.containerId === c._id,
          );
          days = finalizedContainer?.days ?? 0;
        }

        return {
          ...acc,
          [c._id]: days,
        };
      },
      {} as { [containerId: string]: number },
    );
  }, [containers, order.containers, order.finalizedLineItems]);

  // Determine initial weight based on pricing model
  const getInitialWeight = () => {
    // For bundle and fixed pricing, always start at 0
    if (pricingModel === PricingTypeEnum.BUNDLE || pricingModel === PricingTypeEnum.FIXED) {
      return 0;
    }
    // For pay-as-you-go, use existing logic
    return Math.max(initialWeight, minWeightInTons);
  };

  // States with stable references
  const [state, setState] = useState({
    openDiscountDialog: false,
    weight: getInitialWeight(),
    additionalMaterials: {} as { [materialId: string]: number },
    usageByContainer: initialUsageByContainer,
  });

  // Update weight when minimum weight changes
  useEffect(() => {
    const newWeight =
      pricingModel === PricingTypeEnum.BUNDLE || pricingModel === PricingTypeEnum.FIXED
        ? 0
        : Math.max(initialWeight, minWeightInTons);

    setState((prevState) => ({
      ...prevState,
      weight: newWeight,
    }));
  }, [initialWeight, minWeightInTons, pricingModel]);

  // Query with proper caching and enabled flag
  const { data: materials } = trpc.admin.material.getMaterials.useQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    suspense: false,
    // Only fetch when component mounts
    enabled: true,
    // Prevent background refetches
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const utils = trpc.useUtils();

  // Memoize mutation callback
  const { isPending, mutate } = trpc.admin.order.adminFinalizeOrder.useMutation({
    onSuccess: () => {
      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(order._id);
    },
    onError: () => {
      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(order._id);
    },
  });

  // Memoize state updates
  const updateState = useCallback((updates: Partial<typeof state>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Create pricing context based on the pricing model
  const pricingContext = useMemo((): PricingContext => {
    const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
    const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;
    const periodTier = containerPricing?.periodTier;

    switch (pricingModel) {
      case PricingTypeEnum.FIXED:
        return {
          pricingModel: PricingTypeEnum.FIXED,
          includedDays: containerPricing?.includedDays || 0,
          dailyOverageFeeInCents: containerPricing?.dailyOverageFeeInCents || 0,
          perTonRateInCents: materialPricing?.perTonRateInCents || order.pricePerTonInCents || 0,
        };
      case PricingTypeEnum.BUNDLE:
        return {
          pricingModel: PricingTypeEnum.BUNDLE,
          includedDays: periodTier?.selectedPeriodDays || 0,
          includedTonnage: periodTier?.includedTonnage || 0,
          dailyOverageFeeInCents:
            periodTier?.additionalDayRateInCents || containerPricing?.adjustedPriceInCents || 0,
          tonOverageInCents:
            periodTier?.additionalTonRateInCents || materialPricing?.perTonRateInCents || 0,
        };
      case PricingTypeEnum.PAY_AS_YOU_GO:
      default: {
        const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
        const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;

        return {
          pricingModel: PricingTypeEnum.PAY_AS_YOU_GO,
          perTonRateInCents: materialPricing?.perTonRateInCents || order.pricePerTonInCents || 0,
          dailyRateInCents:
            containerPricing?.totalDays && containerPricing?.adjustedPriceInCents
              ? Math.round(containerPricing.adjustedPriceInCents / containerPricing.totalDays)
              : 0,
        };
      }
    }
  }, [order.quotedPricingDetails, order.pricePerTonInCents, pricingModel]);

  // Memoize adjustments calculation with stable dependencies
  const adjustments = useMemo(() => {
    if (!materials)
      return {
        materialsAdjustment: 0,
        containerAdjustment: 0,
        weightAdjustment: 0,
        containerRefundDue: 0,
      };

    return getPricingAwareOrderAdjustments({
      order: { ...order, cart },
      usageByContainer: state.usageByContainer,
      additionalMaterials: state.additionalMaterials,
      finalWeight: state.weight,
      finalContainers: containers,
      allAvailableMaterials: materials,
      pricingContext,
    });
  }, [materials, order, cart, containers, state, pricingContext]);

  // Calculate the total with adjustments for discount calculations
  const totalWithAdjustments = useMemo(() => {
    const { materialsAdjustment, containerAdjustment, weightAdjustment } = adjustments;
    const originalTotal = cart?.lineItems?.totalInCents || 0;
    return originalTotal + materialsAdjustment + containerAdjustment + weightAdjustment;
  }, [adjustments, cart?.lineItems?.totalInCents]);

  // Memoize the mutation handler with stable state reference
  const handleFinalize = useCallback(
    (balanceDue: number) => {
      mutate({
        orderId: order._id!,
        totalWeightInTons: state.weight,
        containers: Object.keys(state.usageByContainer).map((containerId) => ({
          containerId,
          days: state.usageByContainer[containerId],
        })),
        expectedFinalBalance: balanceDue,
        additionalMaterialCharges: Object.keys(state.additionalMaterials).map((materialId) => ({
          materialId,
          quantity: state.additionalMaterials[materialId],
        })),
      });
    },
    [mutate, order._id!, state],
  );

  const { materialsAdjustment, containerAdjustment, weightAdjustment, containerRefundDue } =
    adjustments;

  // Create ChargeSummary props using utility
  const chargeSummaryProps = useMemo(
    () =>
      createChargeSummaryPropsFromWasteProcessing(
        order,
        cart,
        {
          weightAdjustment,
          materialsAdjustment,
          containerAdjustment,
          containerRefundDue,
        },
        state,
        containers,
      ),
    [
      order,
      cart,
      weightAdjustment,
      materialsAdjustment,
      containerAdjustment,
      containerRefundDue,
      state,
      containers,
    ],
  );

  // Render pricing form based on pricing model
  const renderPricingForm = () => {
    const commonProps = {
      order,
      containers,
      cart,
      state,
      updateState,
    };

    switch (pricingModel) {
      case PricingTypeEnum.BUNDLE:
        return <BundlePricingForm {...commonProps} />;
      case PricingTypeEnum.FIXED:
        return <FixedPricingForm {...commonProps} />;
      case PricingTypeEnum.PAY_AS_YOU_GO:
      default:
        return <PayAsYouGoPricingForm {...commonProps} organization={organization} />;
    }
  };
  // Render with memoized props
  return (
    <div className="card flex flex-col gap-6 justify-start items-start w-full">
      {renderPricingForm()}

      <div className="h-[1px] w-full bg-disabled"></div>
      <ChargeSummary {...chargeSummaryProps}>
        {({ balanceDue }) => (
          <Button
            className="w-full mt-6"
            disabled={isPending}
            onClick={() => handleFinalize(balanceDue)}
          >
            <div className="flex items-center gap-2">
              {isPending ? <Loader h={4} w={4} color="text-white" /> : null}
              {`${
                MoneyFromCents(balanceDue).isZero()
                  ? 'Mark Order Complete'
                  : MoneyFromCents(balanceDue).isNegative()
                    ? 'Refund Remaining Balance'
                    : 'Charge Remaining Balance'
              }`}
            </div>
          </Button>
        )}
      </ChargeSummary>
      <DiscountDialog
        discounts={(order.discounts || [])
          .filter(
            (d) => (d.percentage > 0 || d.amountInCents > 0) && d.type && d.type.trim().length > 0,
          )
          .map((d) =>
            Discount({
              amount: d.percentage > 0 ? d.percentage : d.amountInCents,
              type: d.percentage > 0 ? 'percent' : 'dollars',
            }),
          )}
        orderTotal={MoneyFromCents(totalWithAdjustments)}
        onClose={() => updateState({ openDiscountDialog: false })}
        open={state.openDiscountDialog}
        showTotalTable={order.status !== OrderStatusEnum.WasteProcessing}
        orderId={order._id!}
      />
    </div>
  );
};
